import type { CareReviewListStatus, CareReviewTodo } from "@/lib/api/care";
import { buildRedactedPseudoId } from "@/lib/redacted-display";
import { DateTime } from "luxon";

export const CARE_REVIEW_PAGE_SIZE = 15;

export type CareReviewSortColumn =
  | "mentee"
  | "cycle"
  | "age"
  | "submitted"
  | "assigned";

export type CareReviewSort = {
  column: CareReviewSortColumn;
  direction: "asc" | "desc";
};

export function daysOpen(
  createdAt: string,
  submittedAt: string | null,
): number | null {
  if (!submittedAt) return null;
  const start = DateTime.fromISO(createdAt);
  const end = DateTime.fromISO(submittedAt);
  if (!start.isValid || !end.isValid) return null;
  return Math.max(0, Math.floor(end.diff(start, "days").days));
}

export function presentationChanged(
  review: Pick<CareReviewTodo, "recommended" | "current">,
): boolean {
  if (!review.recommended || !review.current) return true;
  return review.recommended.code !== review.current.code;
}

export function menteeListLabel(
  mentee: { id: string; name: string | null },
  hideIdentity: boolean,
  fallback = "Teen",
): string {
  if (hideIdentity) return buildRedactedPseudoId(mentee.id);
  return mentee.name ?? fallback;
}

export function menteeMatchesSearch(
  mentee: { id: string; name: string | null },
  query: string,
  hideIdentity: boolean,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = hideIdentity
    ? buildRedactedPseudoId(mentee.id)
    : (mentee.name ?? "");
  return haystack.toLowerCase().includes(needle);
}

export function defaultSort(status: CareReviewListStatus): CareReviewSort {
  return status === "open"
    ? { column: "age", direction: "asc" }
    : { column: "submitted", direction: "desc" };
}
